/**
 * How many BARS to ask `/charts/history/` for, when a window is a span of
 * trading rather than of calendar.
 *
 * ── Why a bar count and not a date window ────────────────────────────────────
 *
 * `/charts/history/` takes `from` / `to` (epoch seconds) or `countback`, never
 * a range key: the UDF datafeed it ports speaks windows, not "1Y". The two are
 * not equivalent.
 *
 * `countback` is "the N most recent drawable bars before `to`" and wins over
 * `from` (mm-be `services/chart/bars.py`). It is used because a calendar
 * window cannot promise a number of sessions on this exchange. NEPSE shuts for
 * the best part of two weeks at Dashain, plus a long tail of public holidays:
 * a `from = today − 365 days` request comes back short by however many
 * sessions the closures ate, while `countback` returns the last N sessions
 * whenever they happened.
 *
 * ── How the counts are derived ───────────────────────────────────────────────
 *
 * From mm-be's own chart vocabulary, so the two repos cannot drift apart:
 * `RANGE_DAYS = {"1W": 7, "1M": 30, "3M": 91, "1Y": 365, "ALL": None}`
 * (`services/market/indices.py`), converted at NEPSE's five trading days a
 * week (Sunday to Thursday).
 *
 * ── What that conversion does NOT account for, stated plainly ────────────────
 *
 * Public holidays. NEPSE closes for considerably more of them than five-a-week
 * arithmetic assumes, so a year does not actually run to 261 sessions and a
 * year of bars reaches back somewhat FURTHER than twelve months of calendar.
 * Discounting it would mean either a NEPSE holiday calendar (this app has
 * none, and mm-be publishes none) or a guessed number, and a guessed discount
 * is a worse error than a stated, derivable overshoot — it would look precise
 * while being invented.
 */

/** NEPSE's trading week: Sunday to Thursday. */
export const SESSIONS_PER_WEEK = 5

/** Calendar days as trading sessions, rounded to the nearest whole bar. */
export const sessionsIn = (calendarDays: number): number =>
  Math.max(1, Math.round((calendarDays * SESSIONS_PER_WEEK) / 7))

/** A year of sessions — what the stock Technical panel reads, because its
 *  200-day average needs two hundred bars. */
export const SESSIONS_IN_YEAR = sessionsIn(365)
